<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';

const props = defineProps({
  installments: { type: Array, default: () => [] },
  downloadingId: { type: [Number, String], default: null },
  error: { type: String, default: '' },
});
const emit = defineEmits(['download']);
const { t } = useI18n();
const hasDocuments = computed(() =>
  props.installments.some(installment => installment.planilhas?.length)
);
const formatDate = value => {
  if (!value) return t('SALES.DETAIL.EMPTY_VALUE');
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC' }).format(date);
};
</script>

<template>
  <LaisCard
    :title="t('SALES.DETAIL.COMMISSION.SHEETS.TITLE')"
    prefix-icon="i-lucide-files"
  >
    <p class="mb-4 text-sm text-lais-text-muted">
      {{ t('SALES.DETAIL.COMMISSION.SHEETS.SUBTITLE') }}
    </p>
    <p
      v-if="error"
      class="mb-4 rounded-lais-sm border border-red-200 bg-red-50 p-3 text-sm text-red-700"
    >
      {{ error }}
    </p>
    <p v-if="!hasDocuments" class="text-sm text-lais-text-muted">
      {{ t('SALES.DETAIL.COMMISSION.SHEETS.REVIEWING') }}
    </p>
    <div v-else class="grid gap-3">
      <article
        v-for="installment in installments"
        :key="installment.id"
        class="grid gap-3 rounded-lais border border-lais-border-soft bg-lais-bg-soft p-4 sm:grid-cols-[auto_1fr] sm:items-center"
      >
        <span
          class="w-fit rounded-full bg-lais-primary-50 px-2 py-1 text-xs font-bold text-lais-primary"
        >
          {{
            t('SALES.DETAIL.COMMISSION.SHEETS.INSTALLMENT', {
              number: installment.numero_parcela,
            })
          }}
        </span>
        <div class="grid gap-2">
          <p
            v-if="!installment.planilhas?.length"
            class="text-sm text-lais-text-muted"
          >
            {{ t('SALES.DETAIL.COMMISSION.SHEETS.REVIEWING') }}
          </p>
          <div
            v-for="sheet in installment.planilhas"
            :key="sheet.id"
            class="flex flex-wrap items-center justify-between gap-3 text-sm"
          >
            <div>
              <p class="font-semibold text-lais-text">
                {{ sheet.nome_original }}
              </p>
              <p class="text-xs text-lais-text-muted">
                {{ formatDate(sheet.created_at) }}
              </p>
            </div>
            <button
              type="button"
              class="font-semibold text-lais-primary underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="
                !sheet.download_available || downloadingId === sheet.id
              "
              @click="emit('download', { installment, sheet })"
            >
              {{
                downloadingId === sheet.id
                  ? t('SALES.DETAIL.COMMISSION.SHEETS.DOWNLOADING')
                  : t('SALES.DETAIL.COMMISSION.SHEETS.DOWNLOAD')
              }}
            </button>
          </div>
        </div>
      </article>
    </div>
  </LaisCard>
</template>
